<template>
  <div class="page-view">
    <div class="header-row">
      <div>
        <h2>接口调用日志</h2>
        <p>查看上游开放接口的调用记录、响应结果和错误信息。</p>
      </div>
      <el-button type="primary" plain @click="loadLogs">刷新</el-button>
    </div>

    <div class="brand-card filter-card">
      <div class="toolbar-row">
        <el-input v-model.trim="filters.keyword" placeholder="应用 / 接口 / IP / 错误 / 内容" clearable />
        <el-input v-model.trim="filters.app_id" placeholder="AppID" clearable />
        <el-select v-model="filters.app_type" placeholder="应用类型" clearable>
          <el-option label="上游" value="上游" />
          <el-option label="下游" value="下游" />
        </el-select>
        <el-select v-model="filters.success" placeholder="调用结果" clearable>
          <el-option label="成功" :value="true" />
          <el-option label="失败" :value="false" />
        </el-select>
        <el-input v-model.trim="filters.status_code" placeholder="状态码" clearable />
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          unlink-panels
        />
        <el-button type="primary" plain @click="handleQuery">查询</el-button>
      </div>
    </div>

    <div class="brand-card table-card">
      <el-table :data="logs" stripe>
        <el-table-column prop="created_at" label="调用时间" min-width="180" />
        <el-table-column label="应用" min-width="220">
          <template slot-scope="{ row }">
            <div>{{ row.app_name || "-" }}</div>
            <div class="muted">{{ row.app_type || "-" }} / {{ row.app_id || "-" }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="method" label="方法" width="90" />
        <el-table-column prop="path" label="接口路径" min-width="260" show-overflow-tooltip />
        <el-table-column label="结果" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.success ? 'success' : 'danger'">{{ row.success ? "成功" : "失败" }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status_code" label="状态码" width="90" />
        <el-table-column prop="duration_ms" label="耗时(ms)" width="100" />
        <el-table-column prop="ip" label="IP" min-width="140" />
        <el-table-column prop="error_message" label="错误信息" min-width="220" show-overflow-tooltip />
        <el-table-column label="详情" width="90" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openDetail(row)">查看</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pager-row">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :page-size="pager.page_size"
          :current-page="pager.page"
          :page-sizes="[10, 20, 50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog title="接口调用详情" :visible.sync="showDetail" width="760px">
      <div v-if="currentLog" class="log-detail">
        <div class="detail-grid">
          <div><span>应用</span><strong>{{ currentLog.app_name || "-" }}</strong></div>
          <div><span>AppID</span><strong>{{ currentLog.app_id || "-" }}</strong></div>
          <div><span>接口</span><strong>{{ currentLog.method }} {{ currentLog.path }}</strong></div>
          <div><span>结果</span><strong>{{ currentLog.success ? "成功" : "失败" }} / {{ currentLog.status_code }}</strong></div>
          <div><span>IP</span><strong>{{ currentLog.ip || "-" }}</strong></div>
          <div><span>耗时</span><strong>{{ currentLog.duration_ms }} ms</strong></div>
        </div>
        <h4>请求内容</h4>
        <pre>{{ formatJson(currentLog.request_body) }}</pre>
        <h4>响应内容</h4>
        <pre>{{ formatJson(currentLog.response_body) }}</pre>
        <h4 v-if="currentLog.error_message">错误信息</h4>
        <pre v-if="currentLog.error_message">{{ currentLog.error_message }}</pre>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchUpstreamApiLogs } from "../api/orders";

export default {
  data() {
    return {
      filters: {
        keyword: "",
          app_id: "",
          app_type: "",
          success: "",
        status_code: "",
      },
      dateRange: [],
      logs: [],
      total: 0,
      pager: { page: 1, page_size: 10 },
      showDetail: false,
      currentLog: null,
    };
  },
  mounted() {
    this.loadLogs();
  },
  methods: {
    async loadLogs() {
      const [created_start, created_end] = this.dateRange || [];
      const statusCode = this.filters.status_code ? Number(this.filters.status_code) : undefined;
      const { data } = await fetchUpstreamApiLogs({
        keyword: this.filters.keyword || undefined,
        app_id: this.filters.app_id || undefined,
        app_type: this.filters.app_type || undefined,
        success: this.filters.success === "" ? undefined : this.filters.success,
        status_code: Number.isFinite(statusCode) ? statusCode : undefined,
        created_start: created_start || undefined,
        created_end: created_end || undefined,
        page: this.pager.page,
        page_size: this.pager.page_size,
      });
      this.logs = data.items || [];
      this.total = data.total || 0;
    },
    handleQuery() {
      this.pager.page = 1;
      this.loadLogs();
    },
    onPageChange(page) {
      this.pager.page = page;
      this.loadLogs();
    },
    onSizeChange(size) {
      this.pager.page = 1;
      this.pager.page_size = size;
      this.loadLogs();
    },
    openDetail(row) {
      this.currentLog = row;
      this.showDetail = true;
    },
    formatJson(value) {
      if (!value) return "-";
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch (error) {
        return value;
      }
    },
  },
};
</script>

<style scoped>
.muted {
  color: #8a98ad;
  font-size: 12px;
}

.log-detail {
  display: grid;
  gap: 14px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 18px;
}

.detail-grid span {
  display: block;
  margin-bottom: 4px;
  color: #8a98ad;
  font-size: 12px;
}

.detail-grid strong {
  color: #27364f;
  font-size: 13px;
  word-break: break-all;
}

.log-detail h4 {
  margin: 4px 0 0;
  color: #27364f;
}

.log-detail pre {
  max-height: 260px;
  margin: 0;
  padding: 12px;
  overflow: auto;
  border-radius: 6px;
  background: #f6f8fb;
  color: #27364f;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
